/**
 * AgencySettings.jsx — Sprint 7.
 *
 * Three sub-sections accessed via top tabs:
 *  - Profile: edit the marketplace listing (admin-only).
 *  - Password: change own password (any teammate).
 *  - Bundles:  rate cards visible on the public agency profile (admin-only).
 */
import React, { useEffect, useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import {
  UserCircle, Lock, Package, Plus, Trash, Check, Star, CaretDown,
  PencilSimple, X, ArrowsClockwise, Storefront,
} from '@phosphor-icons/react';
import CatalogPicker from '../components/CatalogPicker';
import LogoUploader from '../components/LogoUploader';

const ACCENT = '#6366f1';

const TABS = [
  { id: 'profile',  label: 'Profile',  icon: UserCircle, admin: true,  testid: 'agency-settings-tab-profile' },
  { id: 'services', label: 'Services', icon: Storefront, admin: true,  testid: 'agency-settings-tab-services' },
  { id: 'password', label: 'Password', icon: Lock,       admin: false, testid: 'agency-settings-tab-password' },
  { id: 'bundles',  label: 'Bundles',  icon: Package,    admin: true,  testid: 'agency-settings-tab-bundles' },
];

const AgencySettings = ({ user }) => {
  const isAdmin = user?.agency_role === 'admin';
  const [tab, setTab] = useState(isAdmin ? 'profile' : 'password');

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1080px] mx-auto" data-testid="agency-settings">
      <div className="mb-6">
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2" style={{ color: ACCENT }}>Command Centre</div>
        <h1 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary leading-[1.05] mb-2" style={{ letterSpacing: '-0.02em' }}>
          Settings
        </h1>
        <p className="text-sm text-text-secondary">Update your marketplace listing, change your password, and manage the rate cards founders see.</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <div className="flex items-center gap-1 overflow-x-auto">
          {TABS.filter((t) => !t.admin || isAdmin).map((t) => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} data-testid={t.testid} className={`relative px-4 py-2.5 text-sm font-medium inline-flex items-center gap-1.5 ${active ? 'text-text-primary' : 'text-text-muted hover:text-text-secondary'}`}>
                <Icon size={13} weight="bold" /> {t.label}
                {active && <span className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: ACCENT }} />}
              </button>
            );
          })}
        </div>
      </div>

      {tab === 'profile'  && isAdmin && <ProfileTab />}
      {tab === 'services' && isAdmin && (
        <div className="bg-surface border border-border rounded-2xl p-5" data-testid="agency-settings-services-panel">
          <div className="mb-4">
            <h2 className="text-base font-heading font-bold mb-1">My services</h2>
            <p className="text-xs text-text-muted">Pick from the agency catalog so founders can find you when they search for what they need. Founders see your name + price; the catalog naming stays canonical so search works across all agencies.</p>
          </div>
          <CatalogPicker category="agency" accent={ACCENT} listEndpoint="/agencies/me" saveEndpoint="/agency/me/catalog-offerings" parentKey="agency" />
        </div>
      )}
      {tab === 'password' && <PasswordTab />}
      {tab === 'bundles'  && isAdmin && <BundlesTab />}
    </div>
  );
};

/* ---------- Profile ---------- */
const ProfileTab = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get('/agencies/me').then((r) => setForm(r.data.agency)).catch((e) => toast.error(e.response?.data?.detail || 'Load failed'));
    // eslint-disable-next-line
  }, []);
  if (!form) return <div className="text-sm text-text-muted">Loading…</div>;
  const patch = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await api.patch('/agencies/me', {
        name: form.name,
        tagline: form.tagline,
        description: form.description,
        services: form.services,
        sector_focus: form.sector_focus,
        stage_focus: form.stage_focus,
        pricing_min: form.pricing_min,
        pricing_max: form.pricing_max,
        pricing_currency: form.pricing_currency,
        turnaround_days: form.turnaround_days,
        website: form.website,
        contact_email: form.contact_email,
      });
      toast.success('Profile updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4 max-w-2xl" data-testid="agency-settings-profile">
      <LogoUploader
        logoFileId={form.logo_file_id}
        accent={ACCENT}
        testidPrefix="agency-logo"
        onUploaded={async (fileId) => {
          // (Iter 51) Persist agency logo immediately, then update local form.
          await api.patch('/agencies/me', { logo_file_id: fileId });
          setForm((p) => ({ ...p, logo_file_id: fileId }));
        }}
      />
      <Field label="Agency name *"           value={form.name || ''}        onChange={(v) => patch('name', v)} testid="agency-profile-name" />
      <Field label="Tagline"                  value={form.tagline || ''}     onChange={(v) => patch('tagline', v)} testid="agency-profile-tagline" />
      <TextArea label="Description"           value={form.description || ''} onChange={(v) => patch('description', v)} rows={4} testid="agency-profile-description" />
      <ChipList label="Services"              value={form.services || []}    onChange={(v) => patch('services', v)} testid="agency-profile-services" placeholder="Add a service" />
      <ChipList label="Sector focus"          value={form.sector_focus || []} onChange={(v) => patch('sector_focus', v)} testid="agency-profile-sectors" placeholder="Add a sector" />
      <ChipList label="Stage focus"           value={form.stage_focus || []}  onChange={(v) => patch('stage_focus', v)} testid="agency-profile-stages" placeholder="Add a stage" />
      <div className="grid grid-cols-3 gap-3">
        <Field label="Pricing min"            type="number" value={form.pricing_min || ''} onChange={(v) => patch('pricing_min', Number(v))} testid="agency-profile-pricing-min" />
        <Field label="Pricing max"            type="number" value={form.pricing_max || ''} onChange={(v) => patch('pricing_max', Number(v))} testid="agency-profile-pricing-max" />
        <Field label="Currency"               value={form.pricing_currency || 'INR'} onChange={(v) => patch('pricing_currency', v)} testid="agency-profile-currency" />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Turnaround (days)"      type="number" value={form.turnaround_days || ''} onChange={(v) => patch('turnaround_days', Number(v))} testid="agency-profile-turnaround" />
        <Field label="Contact email"          value={form.contact_email || ''} onChange={(v) => patch('contact_email', v)} testid="agency-profile-email" />
      </div>
      <Field label="Website"                  value={form.website || ''} onChange={(v) => patch('website', v)} testid="agency-profile-website" />
      <button onClick={save} disabled={busy} data-testid="agency-profile-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
        <Check size={13} weight="bold" /> {busy ? 'Saving…' : 'Save profile'}
      </button>
    </div>
  );
};

/* ---------- Password ---------- */
const PasswordTab = () => {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (form.new_password.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (form.new_password !== form.confirm) { toast.error('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: form.current_password, new_password: form.new_password });
      toast.success('Password updated');
      setForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-3 max-w-md" data-testid="agency-settings-password">
      <Field label="Current password" type="password" value={form.current_password} onChange={(v) => setForm((p) => ({ ...p, current_password: v }))} testid="agency-password-current" />
      <Field label="New password (8+ chars)" type="password" value={form.new_password} onChange={(v) => setForm((p) => ({ ...p, new_password: v }))} testid="agency-password-new" />
      <Field label="Confirm new password" type="password" value={form.confirm} onChange={(v) => setForm((p) => ({ ...p, confirm: v }))} testid="agency-password-confirm" />
      <button onClick={submit} disabled={busy} data-testid="agency-password-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white disabled:opacity-50" style={{ backgroundColor: ACCENT }}>
        <Lock size={13} weight="bold" /> {busy ? 'Updating…' : 'Update password'}
      </button>
    </div>
  );
};

/* ---------- Bundles ---------- */
const BundlesTab = () => {
  const [bundles, setBundles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get('/agencies/me');
        const aid = me.data.agency.id;
        const r = await api.get(`/agencies/${aid}/bundles`);
        if (!cancelled) setBundles(r.data.bundles || []);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const remove = async (b) => {
    if (!window.confirm(`Delete bundle "${b.title}"?`)) return;
    try {
      await api.delete(`/agencies/me/bundles/${b.id}`);
      toast.success('Deleted');
      setReloadKey((k) => k + 1);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-sm text-text-muted inline-flex items-center gap-2"><ArrowsClockwise size={14} className="animate-spin" /> Loading…</div>;
  return (
    <div data-testid="agency-settings-bundles">
      <div className="flex items-center justify-between mb-4">
        <p className="text-xs text-text-muted">These appear on your public agency profile and as quick-start options for founders.</p>
        <button onClick={() => setEditing({})} data-testid="agency-bundles-add" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold text-white" style={{ backgroundColor: ACCENT }}>
          <Plus size={11} weight="bold" /> New bundle
        </button>
      </div>
      {bundles.length === 0 ? (
        <div className="border border-dashed border-border rounded-xl p-10 text-center text-sm text-text-muted">
          <Package size={22} className="mx-auto mb-2 text-text-muted/40" />
          No bundles yet. Add your most popular rate cards so founders know exactly what to expect.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {bundles.map((b) => (
            <div key={b.id} className="bg-surface border border-border rounded-xl p-4" data-testid={`agency-bundle-${b.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-heading font-bold text-text-primary">{b.title}</h3>
                    {b.is_featured && <Star size={11} weight="fill" style={{ color: ACCENT }} />}
                  </div>
                  <div className="text-lg font-mono font-bold text-text-primary">{b.currency} {(b.price || 0).toLocaleString('en-IN')} <span className="text-xs text-text-muted font-normal">{b.duration_label || ''}</span></div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => setEditing(b)} data-testid={`agency-bundle-edit-${b.id}`} className="text-text-muted hover:text-text-primary"><PencilSimple size={12} weight="bold" /></button>
                  <button onClick={() => remove(b)} data-testid={`agency-bundle-delete-${b.id}`} className="text-text-muted hover:text-danger"><Trash size={12} weight="bold" /></button>
                </div>
              </div>
              {b.description && <p className="text-xs text-text-secondary mb-2">{b.description}</p>}
              {b.deliverables?.length > 0 && (
                <ul className="space-y-0.5 text-[11px] text-text-secondary">
                  {b.deliverables.map((d, i) => (
                    <li key={i} className="inline-flex items-center gap-1.5"><Check size={9} weight="bold" style={{ color: ACCENT }} /> {d}</li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
      {editing !== null && <BundleEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); setReloadKey((k) => k + 1); }} />}
    </div>
  );
};

const BundleEditor = ({ initial, onClose, onSaved }) => {
  const isEdit = !!initial.id;
  const [form, setForm] = useState({
    title: initial.title || '',
    description: initial.description || '',
    price: initial.price || 0,
    currency: initial.currency || 'INR',
    duration_label: initial.duration_label || '',
    deliverables: initial.deliverables || [],
    is_featured: initial.is_featured || false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await api.patch(`/agencies/me/bundles/${initial.id}`, form);
        toast.success('Bundle updated');
      } else {
        await api.post('/agencies/me/bundles', form);
        toast.success('Bundle created');
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur z-50 flex items-start justify-center pt-16 p-4 overflow-y-auto" onClick={onClose} data-testid="agency-bundle-modal">
      <div className="bg-surface border border-border rounded-xl p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-heading font-bold text-text-primary text-lg" style={{ letterSpacing: '-0.01em' }}>{isEdit ? 'Edit bundle' : 'New bundle'}</h3>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary"><X size={16} weight="bold" /></button>
        </div>
        <div className="space-y-3">
          <Field label="Title *" value={form.title} onChange={(v) => setForm((p) => ({ ...p, title: v }))} testid="agency-bundle-title" />
          <TextArea label="Description" value={form.description} onChange={(v) => setForm((p) => ({ ...p, description: v }))} rows={3} testid="agency-bundle-description" />
          <div className="grid grid-cols-[1fr_120px_120px] gap-3">
            <Field label="Price *" type="number" value={form.price} onChange={(v) => setForm((p) => ({ ...p, price: Number(v) }))} testid="agency-bundle-price" />
            <Select label="Currency" value={form.currency} onChange={(v) => setForm((p) => ({ ...p, currency: v }))} options={['INR', 'USD']} testid="agency-bundle-currency" />
            <Field label="Cadence" value={form.duration_label} onChange={(v) => setForm((p) => ({ ...p, duration_label: v }))} placeholder="per month" testid="agency-bundle-cadence" />
          </div>
          <ChipList label="Deliverables" value={form.deliverables} onChange={(v) => setForm((p) => ({ ...p, deliverables: v }))} placeholder="e.g. 12 ghostwritten posts" testid="agency-bundle-deliverables" />
          <label className="inline-flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
            <input type="checkbox" checked={form.is_featured} onChange={(e) => setForm((p) => ({ ...p, is_featured: e.target.checked }))} data-testid="agency-bundle-featured" /> Mark as featured
          </label>
        </div>
        <div className="flex gap-3 mt-5">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 text-sm border border-border rounded-lg">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="agency-bundle-save" className="flex-1 px-4 py-2.5 text-sm font-semibold text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: ACCENT }}>{busy ? 'Saving…' : 'Save bundle'}</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- primitives ---------- */
const Field = ({ label, value, onChange, type = 'text', placeholder, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary" />
  </div>
);
const TextArea = ({ label, value, onChange, rows = 3, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={rows} data-testid={testid} className="w-full bg-background border border-border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-text-primary leading-relaxed" />
  </div>
);
const Select = ({ label, value, onChange, options, testid }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="appearance-none w-full bg-background border border-border rounded-lg pl-3 pr-8 py-2 text-sm">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
      <CaretDown size={10} weight="bold" className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none" />
    </div>
  </div>
);
const ChipList = ({ label, value, onChange, placeholder, testid }) => {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (!v) return;
    if (value.includes(v)) { setInput(''); return; }
    onChange([...value, v]);
    setInput('');
  };
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-bold text-text-muted mb-1.5">{label}</label>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map((v, i) => (
          <span key={i} className="bg-surface border border-border rounded-full px-2.5 py-1 text-xs inline-flex items-center gap-1" data-testid={`${testid}-chip-${i}`}>
            {v}
            <button onClick={() => onChange(value.filter((_, j) => j !== i))} className="text-text-muted hover:text-danger"><X size={9} weight="bold" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }} placeholder={placeholder} data-testid={`${testid}-input`} className="flex-1 bg-background border border-border rounded-lg px-3 py-2 text-sm" />
        <button onClick={add} data-testid={`${testid}-add`} className="text-xs font-semibold px-3 py-2 rounded-lg border border-border hover:border-text-primary">Add</button>
      </div>
    </div>
  );
};

export default AgencySettings;
